import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Copy } from 'lucide-react';

import {
  atualizarPerfil,
  copiarAreas,
  type Configuracao,
  type Participante,
  type TratamentoAdvogado,
} from '@/api/secretario';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { tratamentoProvavel } from '@/lib/tratamento';

import { temPreco } from './HonorariosCampos';

import { AdvogadoEmEdicaoProvider, nomeDoParticipante, projetarParaAdvogado } from './advogadoEmEdicao';
import { AreasAtendidasStep } from './AreasAtendidasStep';
import { EscolhaDeTratamento } from './EscolhaDeTratamento';
import { HonorariosGeraisCard } from './HonorariosGeraisCard';
import { ModelosPadrao } from './ModelosPadrao';
import { ProntidaoDoAdvogado } from './ProntidaoDoAdvogado';
import { SalvaAoSair } from './SalvaAoSair';
import { useWizardSave } from './WizardSaveContext';
import { gravarConfigNoCache } from './cacheDaConfig';

interface PerfilDoAdvogadoProps {
  config: Configuracao;
  advogadoId: string;
  /** Volta à lista de advogados; sem ele (o participante no próprio perfil), não há volta. */
  onVoltar?: () => void;
}

/**
 * O perfil de um advogado no Secretário do escritório (docs/PLANO_SECRETARIO_ESCRITORIO.md
 * §4.2–4.4), em três seções planas: as áreas dele, os honorários e modelos (os dele ou,
 * sem os próprios, os do escritório) e como o Secretário o apresenta. Os cartões são os
 * do Secretário pessoal, dentro do contexto do advogado — que os faz ler e gravar no
 * perfil dele.
 */
export function PerfilDoAdvogado({ config, advogadoId, onVoltar }: PerfilDoAdvogadoProps) {
  const participante = config.participantes?.find((p) => p.usuario_id === advogadoId);
  // Sair do perfil salva o que estava digitado (``SalvaAoSair``).
  const voltar = () => onVoltar?.();
  const [aberta, setAberta] = useState<'honorarios' | 'modelos' | null>(null);
  const abrir = (secao: NonNullable<typeof aberta>) => (o: boolean) => setAberta(o ? secao : null);

  if (!participante) {
    return <p className="text-sm text-muted-foreground">Este advogado não participa do Secretário.</p>;
  }
  const nome = nomeDoParticipante(participante);
  const projetada = projetarParaAdvogado(config, advogadoId);
  // A mesma regra do backend (``honorarios_efetivos``): honorários sem preço não valem.
  const herdaHonorarios = !temPreco(participante.honorarios);
  const contratoProprio = !!participante.template_contrato_id;
  const procuracaoPropria = !!participante.template_procuracao_id;
  const herdaModelos = !contratoProprio && !procuracaoPropria;
  const escritorioTemHonorarios = temPreco(config.honorarios);
  const escritorioTemModelos = !!config.template_contrato_id || !!config.template_procuracao_id;
  const temArea = projetada.areas.some((a) => a.ativo);

  return (
    <AdvogadoEmEdicaoProvider
      value={{
        id: advogadoId,
        nome,
        herdaHonorarios,
        herdaModelos,
        contratoProprio,
        procuracaoPropria,
        escritorioTemHonorarios,
        escritorioTemModelos,
      }}
    >
      <SalvaAoSair configuracaoId={config.id}>
        <div className="space-y-4">
          {onVoltar && (
            <Button type="button" variant="ghost" className="-ml-3 min-h-11" onClick={voltar}>
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              Advogados
            </Button>
          )}
          <h2 className="text-lg font-semibold">{nome}</h2>
          <ProntidaoDoAdvogado participante={participante} nome={nome} secretarioAtivo={config.ativo} />
          <AreasAtendidasStep
            config={projetada}
            isFirst
            onBack={voltar}
            onNext={voltar}
            inicio={!temArea && participante.ativo ? <CopiarAreas config={config} advogadoId={advogadoId} /> : null}
          />
          <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
            <Card>
              <CardHeader>
                <CardTitle>Honorários e modelos</CardTitle>
                <CardDescription>
                  {!escritorioTemHonorarios && !escritorioTemModelos
                    ? 'O escritório ainda não tem os seus: defina aqui ou no padrão do escritório.'
                    : 'O que não for próprio vem do escritório.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Remontados ao trocar a herança: o formulário parte do que vale agora. */}
                <HonorariosGeraisCard
                  key={`honorarios-${herdaHonorarios}`}
                  config={projetada}
                  open={aberta === 'honorarios'}
                  onOpenChange={abrir('honorarios')}
                  onSaved={() => setAberta(null)}
                />
                <ModelosPadrao
                  key={`modelos-${contratoProprio}-${procuracaoPropria}`}
                  config={projetada}
                  open={aberta === 'modelos'}
                  onOpenChange={abrir('modelos')}
                  onSaved={() => setAberta(null)}
                />
              </CardContent>
            </Card>
            <Apresentacao participante={participante} />
          </div>
        </div>
      </SalvaAoSair>
    </AdvogadoEmEdicaoProvider>
  );
}

/**
 * Como o Secretário apresenta o advogado ao cliente — na escolha do advogado e no
 * contrato. Sempre Dr. ou Dra. (Adriano, 28/10): sem escolha salva, o palpite pelo
 * prenome vira uma frase a confirmar num toque; o núcleo do Secretário não deduz
 * gênero (``lib/tratamento``), então só o que alguém confirmou vale. Nome ambíguo
 * abre direto a escolha.
 */
function Apresentacao({ participante }: { participante: Participante }) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const salvo = participante.tratamento ?? null;
  const palpite = salvo ?? tratamentoProvavel(participante.nome) ?? '';
  const [editando, setEditando] = useState(!palpite);
  const [tratamento, setTratamento] = useState<string>(palpite);
  const [nomeTratamento, setNomeTratamento] = useState(participante.nome_tratamento ?? '');

  const salvar = useMutation({
    mutationFn: (dados: { tratamento: string; nome_tratamento: string | null }) =>
      atualizarPerfil(participante.usuario_id, {
        tratamento: dados.tratamento as TratamentoAdvogado,
        nome_tratamento: dados.nome_tratamento,
      }),
    onSuccess: (atualizada) => {
      gravarConfigNoCache(queryClient, atualizada);
      setEditando(false);
      showSuccess('Salvo.');
    },
    onError: (error) => notifyMutationError(error, showError),
  });

  // O que foi digitado e não salvo vai junto ao sair do perfil (``SalvaAoSair``).
  const registerFlush = useWizardSave();
  const { mutate, isPending } = salvar;
  const alterado =
    editando &&
    !!tratamento &&
    (tratamento !== (salvo ?? '') || (nomeTratamento.trim() || null) !== (participante.nome_tratamento ?? null));
  const chave = `apresentacao-${participante.usuario_id}`;
  useEffect(() => {
    registerFlush(
      chave,
      alterado && !isPending ? () => mutate({ tratamento, nome_tratamento: nomeTratamento.trim() || null }) : null,
    );
    return () => registerFlush(chave, null);
  }, [alterado, isPending, mutate, registerFlush, chave, tratamento, nomeTratamento]);

  const nome = (salvo ? participante.nome_tratamento : nomeTratamento.trim()) || participante.nome;
  const rotulo = (salvo ?? tratamento) === 'DRA' ? 'Dra.' : 'Dr.';
  const artigo = (salvo ?? tratamento) === 'DRA' ? 'a' : 'o';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Apresentação</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!editando ? (
          <>
            <p className="text-sm">
              O Secretário {salvo ? `${artigo} apresenta` : `vai apresentá-l${artigo}`} como{' '}
              <span className="font-medium">
                {rotulo} {nome}
              </span>
              .
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <Button type="button" variant="outline" className="min-h-11" onClick={() => setEditando(true)}>
                Trocar
              </Button>
              {!salvo && (
                <Button
                  type="button"
                  className="min-h-11"
                  disabled={salvar.isPending}
                  onClick={() => salvar.mutate({ tratamento, nome_tratamento: participante.nome_tratamento ?? null })}
                >
                  {salvar.isPending && <Spinner className="mr-2 text-inherit" />}
                  Confirmar
                </Button>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="space-y-1.5">
              <Label>Tratamento</Label>
              <EscolhaDeTratamento valor={tratamento} onEscolher={setTratamento} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`nome-tratamento-${participante.usuario_id}`}>Nome</Label>
              <Input
                id={`nome-tratamento-${participante.usuario_id}`}
                value={nomeTratamento}
                onChange={(e) => setNomeTratamento(e.target.value)}
                placeholder={participante.nome}
                maxLength={120}
                className="h-11"
              />
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              {salvo && (
                <Button type="button" variant="ghost" className="min-h-11" onClick={() => setEditando(false)}>
                  Cancelar
                </Button>
              )}
              <Button
                type="button"
                className="min-h-11"
                disabled={!tratamento || salvar.isPending}
                onClick={() => salvar.mutate({ tratamento, nome_tratamento: nomeTratamento.trim() || null })}
              >
                {salvar.isPending && <Spinner className="mr-2 text-inherit" />}
                Salvar
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** "Copiar áreas de…" (§4.3), para o advogado que ainda não tem áreas: as de um colega,
 *  com honorários, documentos e perguntas. */
function CopiarAreas({ config, advogadoId }: { config: Configuracao; advogadoId: string }) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const colegas = (config.participantes ?? []).filter(
    (p) =>
      p.ativo &&
      p.usuario_id !== advogadoId &&
      config.areas.some((a) => (a.advogado_responsavel_id ?? config.usuario_id) === p.usuario_id),
  );
  const [de, setDe] = useState<string>('');

  const copiar = useMutation({
    mutationFn: () => copiarAreas(advogadoId, de),
    onSuccess: (atualizada) => {
      gravarConfigNoCache(queryClient, atualizada);
      setDe('');
      showSuccess('Áreas copiadas.');
    },
    onError: (error) => notifyMutationError(error, showError),
  });

  if (colegas.length === 0) return null;
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-dashed p-3 sm:flex-row sm:items-end">
      <div className="flex-1 space-y-1.5">
        <Label htmlFor={`copiar-${advogadoId}`}>Comece pelas áreas de um colega</Label>
        <Select value={de} onValueChange={setDe}>
          <SelectTrigger id={`copiar-${advogadoId}`} className="h-11">
            <SelectValue placeholder="Escolha o colega" />
          </SelectTrigger>
          <SelectContent>
            {colegas.map((c) => (
              <SelectItem key={c.usuario_id} value={c.usuario_id}>
                {nomeDoParticipante(c)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        type="button"
        variant="outline"
        className="min-h-11"
        onClick={() => copiar.mutate()}
        disabled={!de || copiar.isPending}
      >
        {copiar.isPending ? <Spinner className="mr-2 text-inherit" /> : <Copy className="mr-2 h-4 w-4" />}
        Copiar
      </Button>
    </div>
  );
}
